import * as THREE from 'three';
import { ToolBase, ToolPointer } from './tool';
import { add, dot, mul, projectPointOnPlane, sub, Vec3, vec } from '../core/vec';
import { extrudeFace, connectedFaceComponent } from '../core/ops';
import { faceNormal, faceLoopPoints } from '../core/face';
import { AXIS_COLORS } from '../render/preview';
import { formatLength, parseLength } from '../core/units';

/** Push/Pull (P): Fläche ergreifen und in deren Normale herausziehen. */
export class PushPullTool extends ToolBase {
  readonly id = 'pushpull';
  readonly label = 'Push/Pull';
  readonly shortcut = 'P';
  readonly cursor = 'crosshair';
  readonly hint = 'Fläche anklicken und ziehen · Zahl tippen = exakte Distanz · Esc bricht ab';

  private faceId: number | null = null;
  private dragging = false;
  private normal: Vec3 = vec();
  private startRef: Vec3 = vec(); // Flächenzentrum beim Ergreifen
  private distance = 0;
  /** Untergrenze beim Hineindrücken (nicht durch den eigenen Körper hindurch). */
  private minDistance = -Infinity;
  private downAt = { x: 0, y: 0 };

  enter(): void {
    this.faceId = null;
    this.dragging = false;
  }

  exit(): void {
    this.ctx.renderer.hoverFaceId = null;
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    if (!this.dragging) {
      const hit = this.ctx.renderer.pickEditableFace(e.ndcX, e.ndcY, this.ctx.camera.camera);
      this.ctx.renderer.hoverFaceId = hit;
      this.ctx.requestUpdate();
      return;
    }
    // Cursor auf kameraparalleler Ebene durch das Startzentrum, projiziert auf die Flächennormale
    const camDir = this.cameraDir();
    const p = projectPointOnPlane(e.ray.origin, e.ray.dir, this.startRef, camDir);
    if (!p) return;
    this.distance = Math.max(this.minDistance, dot(sub(p, this.startRef), this.normal));
    this.drawPreview();
    this.ctx.ui.setStatus(`Distanz: ${formatLength(this.distance, this.ctx.units())} · Zahl tippen = exakte Distanz`);
    this.ctx.requestUpdate();
  }

  private cameraDir(): Vec3 {
    const t = new THREE.Vector3();
    this.ctx.camera.camera.getWorldDirection(t);
    return vec(t.x, t.y, t.z);
  }

  private drawPreview(): void {
    if (!this.faceId) return;
    const face = this.ctx.model.faces.get(this.faceId);
    if (!face) return;
    const outer = faceLoopPoints(this.ctx.model, face);
    const top = outer.map((p) => add(p, mul(this.normal, this.distance)));
    this.ctx.preview.polyline(top, 0x333333, true);
    for (let i = 0; i < outer.length; i++) {
      this.ctx.preview.line(outer[i]!, top[i]!, AXIS_COLORS[2]);
    }
    this.ctx.preview.faceOverlay(top, 0xffe9a8);
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    // Klick – Bewegen – Klick (wie SketchUp): zweiter Klick übernimmt
    if (this.dragging) {
      this.finish();
      return;
    }
    const hit = this.ctx.renderer.pickEditableFace(e.ndcX, e.ndcY, this.ctx.camera.camera);
    if (hit === null) return;
    const face = this.ctx.model.faces.get(hit);
    if (!face) return;
    this.faceId = hit;
    const outer = faceLoopPoints(this.ctx.model, face);
    this.normal = faceNormal(outer);
    this.startRef = {
      x: outer.reduce((s, p) => s + p.x, 0) / outer.length,
      y: outer.reduce((s, p) => s + p.y, 0) / outer.length,
      z: outer.reduce((s, p) => s + p.z, 0) / outer.length
    };
    // Teil eines Körpers: höchstens bis kurz vor die gegenüberliegende Seite hineindrücken
    const component = connectedFaceComponent(this.ctx.model, hit);
    this.minDistance = -Infinity;
    if (component.size > 1) {
      let deepest = 0;
      for (const fid of component) {
        const f = this.ctx.model.faces.get(fid);
        if (!f) continue;
        for (const v of f.outer) deepest = Math.min(deepest, dot(sub(this.ctx.model.vertexPosition(v), this.startRef), this.normal));
      }
      if (deepest < -1e-6) this.minDistance = deepest + 1e-3;
    }
    this.dragging = true;
    this.distance = 0;
    this.downAt = { x: e.x, y: e.y };
    this.ctx.ui.setStatus('Ziehen oder Maus bewegen und klicken · Zahl tippen = exakte Distanz');
  }

  pointerUp(e: ToolPointer): void {
    if (!this.dragging) return;
    // Ohne echtes Ziehen losgelassen → Klick-Bewegen-Klick-Modus (kein Mini-Extrudieren durch Zittern)
    if (Math.hypot(e.x - this.downAt.x, e.y - this.downAt.y) < 5) return;
    this.finish();
  }

  private finish(): void {
    if (this.faceId !== null && Math.abs(this.distance) > 1e-6) {
      // Undo-Schritt erst bei echter Änderung
      this.ctx.beginEdit();
      extrudeFace(this.ctx.model, this.faceId, mul(this.normal, Math.max(this.minDistance, this.distance)));
    }
    this.dragging = false;
    this.distance = 0;
    this.faceId = null;
    this.ctx.preview.clear();
    this.ctx.renderer.hoverFaceId = null;
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.dragging;
  }

  vcbDisplay(): string | null {
    if (!this.dragging) return null;
    return formatLength(this.distance, this.ctx.units());
  }

  applyVcb(raw: string): void {
    if (!this.dragging) return;
    const d = parseLength(raw, this.ctx.units());
    if (d === null) return;
    this.distance = d;
    this.finish();
  }

  cancel(): void {
    if (this.dragging) {
      // Es wurde noch nichts am Modell geändert (Extrusion erst beim Loslassen)
      this.dragging = false;
      this.distance = 0;
      this.faceId = null;
      this.ctx.preview.clear();
    }
    this.ctx.renderer.hoverFaceId = null;
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
